/**
 * entities/organization/ui/OrgContextProvider — Active-org bootstrapping.
 *
 * On mount (when authenticated), fetches the user's org list and:
 *   1. If an activeOrgId is already persisted in the store and the org is in
 *      the list, validates it via POST /set-active/ and updates the cached obj.
 *   2. Otherwise defaults to the first org in the list (auto-select).
 *   3. If no orgs exist, clears the active org.
 *
 * This component is rendered inside the authenticated app layout (app-shell).
 * It is a context bootstrap component — it renders its children immediately
 * without waiting for the org fetch (so the app is not blocked by it).
 *
 * The active org setter is also exposed for the OrgSwitcher to call.
 */

import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useOrgStore } from '../model/org-store';
import { useMyOrgsQuery, useSetActiveOrgMutation, MY_ORGS_QUERY_KEY } from '../api/queries';
import type { Organization } from '../model/types';
import { MODULE_ORGANIZATIONS } from '@/shared/config/env';

interface OrgContextProviderProps {
  children: ReactNode;
}

/**
 * Hook that exposes the active-org setter as a callable function.
 * Calling it:
 *   1. Calls POST /api/organizations/set-active/ to validate membership.
 *   2. Updates the Zustand store with the new active org.
 *   3. Invalidates all org-scoped queries so fresh data is fetched.
 *
 * This is the public hook consumed by the OrgSwitcher and create-org flow.
 */
export function useActiveOrg() {
  const store = useOrgStore();
  const setActiveOrgMutation = useSetActiveOrgMutation();
  const queryClient = useQueryClient();

  async function setActiveOrg(org: Organization): Promise<void> {
    // Optimistically update the store so the header is injected immediately.
    store.setActiveOrg(org);
    try {
      const validated = await setActiveOrgMutation.mutateAsync({ organization_id: org.id });
      // Update with the server-confirmed org object.
      store.setActiveOrg(validated);
    } catch (err) {
      // Revert to previous state on failure.
      const previous = store.orgs.find((o) => o.id !== org.id) ?? null;
      if (previous) {
        store.setActiveOrg(previous);
      } else {
        store.clearActiveOrg();
      }
      throw err;
    }
    // Invalidate all org-scoped data so components refetch with the new X-Org-ID header.
    await queryClient.invalidateQueries({ queryKey: MY_ORGS_QUERY_KEY });
  }

  return {
    activeOrgId: store.activeOrgId,
    activeOrg: store.activeOrg,
    orgs: store.orgs,
    setActiveOrg,
    isPending: setActiveOrgMutation.isPending,
  };
}

/**
 * Bootstrap component that hydrates the org store on mount.
 * Renders children immediately without blocking on the org fetch.
 */
export function OrgContextProvider({ children }: OrgContextProviderProps) {
  const { data, isSuccess } = useMyOrgsQuery();
  const store = useOrgStore();
  const setActiveOrgMutation = useSetActiveOrgMutation();

  useEffect(() => {
    if (!MODULE_ORGANIZATIONS) return;
    if (!isSuccess || !data) return;

    const orgs = data.results;
    store.setOrgs(orgs);

    if (orgs.length === 0) {
      store.clearActiveOrg();
      return;
    }

    // Determine which org to activate.
    const persistedId = store.activeOrgId;
    const persistedOrg = persistedId ? orgs.find((o) => o.id === persistedId) : null;
    const targetOrg = persistedOrg ?? orgs[0];

    if (!targetOrg) {
      store.clearActiveOrg();
      return;
    }

    // Only call set-active if we are changing the org or don't have a cached object.
    if (store.activeOrgId === targetOrg.id && store.activeOrg !== null) {
      return;
    }

    // Validate with the backend and update the store.
    void setActiveOrgMutation
      .mutateAsync({ organization_id: targetOrg.id })
      .then((validated) => {
        store.setActiveOrg(validated);
      })
      .catch(() => {
        // If validation fails, just set from the list object (best-effort).
        store.setActiveOrg(targetOrg);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, data?.count]);

  return <>{children}</>;
}
